import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '@environments/environment';

import { CarPaymentOptionsPlans } from '@models/car.model';
import { OnlineFormData } from '@models/online-form.model';
import { Observable, map, catchError, throwError } from 'rxjs';
import { GlobalsService } from './globals.service';
import { OnlineFormAccessoriesResponse, OnlineFormAccessoriesResponseProcessed, OnlineFormAccessoriesUpsaleItemWithId } from '@models/online-form-accessories.model';

@Injectable({
  providedIn: 'root',
})
export class OnlineFormService {
  private http = inject(HttpClient);
  private globalsService = inject(GlobalsService);
  public step: WritableSignal<number> = signal(1);
  public totalSteps: WritableSignal<number> = signal(4);
  public formData: WritableSignal<OnlineFormData> = signal({});
  public accessories: WritableSignal<OnlineFormAccessoriesResponseProcessed | undefined> = signal(undefined);
  public calculatedData: WritableSignal<CarPaymentOptionsPlans | undefined> = signal(undefined);
  public formId: WritableSignal<string | null> = signal(null);

  fetchAccessories(id: number): Observable<OnlineFormAccessoriesResponseProcessed> {
    return this.http.get<OnlineFormAccessoriesResponse>(`${environment.apiNg}/v1/forms/online-sales/settings/?aaaId=${id}`, { headers: this.globalsService.headerHash() }).pipe(
      map((response) => {
        try {
          const accessories: any[] = [];
          const basicWarranty: any[] = [];
          const extendedWarranty: any[] = [];

          Object.keys(response.message.upsale).forEach((key) => {
            const keyNum = parseInt(key, 10);
            const item = response.message.upsale[key as keyof typeof response.message.upsale];

            const itemWithId: OnlineFormAccessoriesUpsaleItemWithId = {
              ...item,
              id: keyNum,
            };

            if (keyNum >= 101 && keyNum <= 199) {
              accessories.push(itemWithId);
            } else if (keyNum >= 201 && keyNum <= 202) {
              basicWarranty.push(itemWithId);
            } else if (keyNum >= 203 && keyNum <= 204) {
              extendedWarranty.push(itemWithId);
            }
          });

          const messageProcessed: OnlineFormAccessoriesResponseProcessed = {
            messageProcessed: {
              ...response.message,
              accessories,
              warranty: {
                basicWarranty,
                extendedWarranty,
              },
            },
          };
          this.accessories.set(messageProcessed);

          return messageProcessed;
        } catch (error) {
          console.error('Error processing accessories data:', error);
          throw new Error('Failed to process accessories data');
        }
      }),
      catchError((error: HttpErrorResponse) => {
        let errorMessage = 'Unknown error occurred';

        if (error.error instanceof ErrorEvent) {
          errorMessage = `Client error: ${error.error.message}`;
        } else {
          errorMessage = `Server error: ${error.status} - ${error.message || error.statusText}`;
        }

        console.error(errorMessage, error);
        return throwError(() => new Error(errorMessage));
      }),
    );
  }

  resetForm(): void {
    this.formId.set(null);
    this.globalsService.token.set('');
    this.globalsService.headerHashLogged.set({});
    this.globalsService.headerHashMD5.set({});
    this.formData.set({});
    this.step.set(1);
  }
}
